import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { styled } from 'styled-components';
import { LuImagePlus } from 'react-icons/lu';
import CloseIcon from '@mui/icons-material/Close';
import { Page, Dashboard, ReflexHorizontalContainer } from '../../templates';
import { Content, Filter } from '../../organisms';
import { SelectAllCheckBox, LanguageButton } from '../../molecules';
import Menu from '../../../utils/menu';
import i18n from '../../../utils/i18n';
import { useTranslation } from 'react-i18next';
import { translateText } from '../../../utils/translateText';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { BackdropPanel, Button, FlexBox, FlexBoxHorizontal, FlexBoxVertical, FlexItem, Label, TextField, TranslateTypography } from '../../atoms';
import palette from '../../../utils/colorPalette';
import { useUser } from '../../../contexts/UserContext';
import Unauthorized from '../../auth/Unauthorized';

const BRD_CD = 'M';

// 서버 검사와 같은 형식. 화면은 안내만 하고 판정은 서버가 한다
const GRAPHIC_CD_RE = /^\d{2}(SS|FW)[NACGFHKIBLM]\d{2}$/;

// 올해 기준 앞뒤 시즌. 새 시즌이 열려도 코드를 고치지 않는다
const seasonOptions = () => {
  const yy = new Date().getFullYear() % 100;
  return [yy + 2, yy + 1, yy, yy - 1].flatMap((y) => [`${y}FW`, `${y}SS`]);
};

const FALLBACK_IMG = 'https://img.icons8.com/?size=100&id=IuQpbmYpl7VN&format=png&color=dadee0';

const EMPTY_FORM = { graphic_cd: '', graphic_nm: '', graphic_dsgnr_nm: '', source_url: '', use_yn: 'Y' };

// 서버가 .jpg로 저장하므로 어떤 이미지든 JPEG으로 바꿔 보낸다
const toJpeg = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(img.src);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('이미지 변환 실패'))), 'image/jpeg', 0.85);
    };
    img.onerror = () => reject(new Error('이미지를 읽을 수 없습니다.'));
    img.src = URL.createObjectURL(file);
  });

function GraphicMaster() {
  const { hasPermission } = useUser();
  const { t } = useTranslation(); // eslint-disable-line no-unused-vars
  const language = i18n.language;
  const [translatedColumns, setTranslatedColumns] = useState(columns);

  useEffect(() => {
    setTranslatedColumns(columns.map((column) => ({
      ...column,
      headerName: translateText({ text: column.headerName, globalLanguage: language }),
    })));
  }, [language]);
  const canEdit = hasPermission('graphicMaster', 'edit');
  const seasons = useMemo(seasonOptions, []);

  const [sesn, setSesn] = useState(seasons[3]); // 내년 SS
  const [types, setTypes] = useState([]); // 비어 있으면 전체
  const [keyword, setKeyword] = useState('');
  const [list, setList] = useState([]);
  const [typeCounts, setTypeCounts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState(null); // 그리드에서 고른 행
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [products, setProducts] = useState([]);
  const [imgVersion, setImgVersion] = useState(0); // 다시 올린 이미지가 캐시에 가리지 않게
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef();

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/graphic/list?brd_cd=${BRD_CD}&sesn=${sesn}`);
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || '그래픽 마스터 조회 실패');
      setList(result.list || []);
      setTypeCounts(result.typeCounts || {});
      return result.list || [];
    } catch (error) {
      console.error(error);
      alert(error.message);
      setList([]);
      setTypeCounts({});
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [sesn]);

  useEffect(() => {
    setTypes([]);
    setSelected(null);
    setIsNew(false);
    fetchList();
  }, [fetchList]);

  // 고른 그래픽의 연결 품번
  useEffect(() => {
    setProducts([]);
    setSelectedFile(null);
    setPreviewImage(null);
    if (!selected) return;
    setForm({
      graphic_cd: selected.graphic_cd,
      graphic_nm: selected.graphic_nm || '',
      graphic_dsgnr_nm: selected.graphic_dsgnr_nm || '',
      source_url: selected.source_url || '',
      use_yn: selected.use_yn || 'Y',
    });
    fetch(`/api/graphic/${selected.graphic_cd}/products`)
      .then((response) => response.json())
      .then((result) => setProducts(result.products || []))
      .catch((error) => console.error(error));
  }, [selected]);

  const rows = list.filter((row) => {
    if (types.length > 0 && !types.some((t) => t.value === row.type)) return false;
    if (!keyword.trim()) return true;
    return `${row.graphic_cd} ${row.graphic_nm || ''} ${row.graphic_dsgnr_nm || ''}`
      .toLowerCase()
      .includes(keyword.trim().toLowerCase());
  });

  const startNew = () => {
    setSelected(null);
    setIsNew(true);
    setForm({ ...EMPTY_FORM, graphic_cd: sesn });
  };

  const save = async () => {
    if (!isNew && !selected) {
      alert('수정할 그래픽을 고르거나 신규 등록을 누르세요.');
      return;
    }
    try {
      const response = await fetch('/api/graphic/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, brd_cd: BRD_CD, sesn, is_new: isNew }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || '그래픽 저장 실패');

      const fresh = await fetchList();
      setIsNew(false);
      setSelected(fresh.find((row) => row.graphic_cd === form.graphic_cd) || null);
    } catch (error) {
      alert(error.message);
    }
  };

  // 파일 선택, 드래그, 붙여넣기 모두 미리보기로 모은다 (CAD 상세 패널과 같은 방식)
  const pickFile = (file) => {
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewImage(e.target.result);
    reader.readAsDataURL(file);
  };
  const handleFileChange = (event) => pickFile(event.target.files[0]);
  const handleDrop = (event) => {
    event.preventDefault();
    pickFile(event.dataTransfer.files[0]);
  };
  const handlePaste = (event) => {
    for (const item of event.clipboardData.items) {
      if (item.type.startsWith('image/')) pickFile(item.getAsFile());
    }
  };
  const handleDeleteImage = () => {
    setSelectedFile(null);
    setPreviewImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadImage = async () => {
    if (!selected) {
      alert('이미지를 올릴 그래픽을 먼저 선택하세요.');
      return;
    }
    if (!selectedFile) {
      alert('업로드할 파일을 선택하세요.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('originalFile', await toJpeg(selectedFile), `${selected.graphic_cd}.jpg`);
      formData.append('graphic_cd', selected.graphic_cd);
      const response = await fetch('/api/graphic/upload-image', { method: 'POST', body: formData });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || '이미지 업로드 실패');
      setImgVersion(Date.now());
      handleDeleteImage();

      const fresh = await fetchList();
      setSelected(fresh.find((row) => row.graphic_cd === selected.graphic_cd) || null);
    } catch (error) {
      alert(error.message);
    }
  };

  const codeHint =
    isNew && form.graphic_cd && !GRAPHIC_CD_RE.test(form.graphic_cd)
      ? '연도 2자리 + SS/FW + 유형 문자 + 순번 2자리 (예: 27SSA13)'
      : isNew && form.graphic_cd.slice(0, 4) !== sesn
        ? `선택한 시즌(${sesn})과 코드의 시즌이 다릅니다`
        : '';

  const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));
  const typeOptions = Object.keys(typeCounts).sort().map((t) => ({ value: t, label: `${t} (${typeCounts[t]})` }));
  const seasonOpts = seasons.map((s) => ({ value: s, label: s }));
  const downWithProducts = form.use_yn === 'N' && products.length > 0;

  return (
    <Page>
      {isLoading ? <BackdropPanel /> : null}
      {!hasPermission('graphicMaster', 'view') ? <Unauthorized title="Graphic Master" brd_cd={BRD_CD} /> :
      <>
      <Menu
        targetPaths={['/interstellar/M/caddata', '/interstellar/M/caddata/pdf', '/interstellar/M/graphic']}
        menuItems={[
          { path: '/interstellar/M/caddata', label: 'CAD Dashboard' },
          { path: '/interstellar/M/caddata/pdf', label: 'CAD PDF View' },
          { path: '/interstellar/M/graphic', label: 'Graphic Master' }
        ]}
      />
      <LanguageButton />
      <Dashboard>
        <h1 style={{ color: '#353339', margin: '30px' }}>Graphic Master</h1>
        <Filter style={{ height: '60px' }}>
          <FlexBoxHorizontal style={{ justifyContent: 'space-between', overflow: 'auto' }}>
            <FlexBox>
              <div style={{ width: '150px', margin: '0 10px 0 20px' }}>
                <SelectAllCheckBox
                  selected={{ value: sesn, label: sesn }}
                  defaultInputValue={{ value: sesn, label: sesn }}
                  setSelected={(option) => option && setSesn(option.value)}
                  options={seasonOpts}
                  title={'시즌'}
                />
              </div>
              <div style={{ width: '220px', marginRight: '10px' }}>
                <SelectAllCheckBox
                  selected={types}
                  setSelected={setTypes}
                  options={typeOptions}
                  title={'유형'}
                  allSelectTitle="유형 All"
                  isMulti
                />
              </div>
              <div style={{ width: '200px', marginRight: '10px' }}>
                <TextField placeholder="코드/이름/담당" onChange={setKeyword} />
              </div>
            </FlexBox>
            <FlexBox>
              {canEdit && (
                <Button label="신규 등록" style={{ minWidth: '90px', marginRight: '20px' }} onClick={startNew} />
              )}
            </FlexBox>
          </FlexBoxHorizontal>
        </Filter>
        <Content
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ReflexHorizontalContainer
            Left={
              <DataGridPremium
                sx={gridStyle}
                columns={translatedColumns}
                rows={rows}
                getRowId={(row) => row.graphic_cd}
                onRowClick={(params) => { setIsNew(false); setSelected(params.row); }}
                hideFooter
                rowHeight={40}
              />
            }
            Right={
              <FlexBoxVertical style={{ justifyContent: 'flex-start', overflowY: 'auto', overflowX: 'hidden', height: '100%' }}>
                  <>
                    {/* 상단부: 코드와 유형 */}
                    <FlexBoxHorizontal style={{ height: 'auto', width: '90%', marginTop: '10px' }}>
                      <Label style={{ width: '45%', margin: '3px' }}>
                        <TranslateTypography variant="content2">{form.graphic_cd || '그래픽 코드'}</TranslateTypography>
                      </Label>
                      <Label style={{ width: '45%', margin: '3px' }}>
                        <TranslateTypography variant="content2">{form.graphic_cd.length >= 5 ? `유형 ${form.graphic_cd[4]}` : '유형'}</TranslateTypography>
                      </Label>
                    </FlexBoxHorizontal>
                    <FlexItem style={{ textAlign: 'center', margin: '10px', fontWeight: 'bold', color: '#353339' }}>
                      <TranslateTypography variant="content1">{form.graphic_nm || '그래픽 이름'}</TranslateTypography>
                    </FlexItem>

                    {/* 상세정보 */}
                    <FlexBoxVertical style={{ height: 'auto', borderBottom: `1.5px solid ${palette.border.default}`, paddingBottom: '10px' }}>
                      {isNew && (
                        <FlexBox style={{ height: 'auto', width: '90%' }}>
                          <TextField
                            defaultValue={form.graphic_cd}
                            placeholder="그래픽 코드 (예: 27SSA13)"
                            onChange={(value) => setForm((prev) => ({ ...prev, graphic_cd: String(value).toUpperCase() }))}
                          />
                        </FlexBox>
                      )}
                      {codeHint && <div style={{ fontSize: '0.7rem', color: '#c0392b', width: '90%' }}>{codeHint}</div>}
                      <FlexBoxHorizontal style={{ height: 'auto', justifyContent: 'space-between', width: '90%' }}>
                        <TextField defaultValue={form.graphic_nm} placeholder="그래픽 이름" onChange={setField('graphic_nm')} readOnly={!canEdit} width={'48%'} />
                        <TextField defaultValue={form.graphic_dsgnr_nm} placeholder="담당 디자이너" onChange={setField('graphic_dsgnr_nm')} readOnly={!canEdit} width={'48%'} />
                      </FlexBoxHorizontal>

                      {/* 이미지 박스 */}
                      <FlexBox style={{ width: '350px', height: selected?.img_url ? '150px' : '100px', position: 'relative', overflow: 'hidden' }}>
                        {/* 이미지가 없으면 다른 화면과 같은 대체 아이콘을 원본 크기(100px)로 */}
                        <img
                          src={selected?.img_url ? `${selected.img_url}?t=${imgVersion}` : FALLBACK_IMG}
                          alt={selected?.graphic_cd || ''}
                          onError={(e) => { e.target.style.width = '100px'; e.target.src = FALLBACK_IMG; }}
                          style={{ width: selected?.img_url ? '100%' : '100px', height: '100%', objectFit: 'contain' }}
                        />
                      </FlexBox>

                      <FlexBox style={{ height: 'auto', width: '90%' }}>
                        <TextField defaultValue={form.source_url} placeholder="팀즈 링크" onChange={setField('source_url')} readOnly={!canEdit} />
                      </FlexBox>
                      {form.source_url && !isNew && (
                        <div style={{ width: '90%', textAlign: 'right' }}>
                          <a href={form.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: '#666' }}>팀즈에서 열기</a>
                        </div>
                      )}
                      {downWithProducts && (
                        <div style={{ fontSize: '0.7rem', color: '#888', width: '90%' }}>미사용으로 내려도 연결된 품번 {products.length}개는 그대로 남습니다</div>
                      )}
                      <FlexBoxHorizontal style={{ height: 'auto', padding: '6px 0', gap: '16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#353339' }}>
                          <input
                            type="checkbox"
                            checked={form.use_yn === 'Y'}
                            disabled={!canEdit}
                            onChange={(e) => setField('use_yn')(e.target.checked ? 'Y' : 'N')}
                            style={{ accentColor: '#666666' }}
                          />
                          사용
                        </label>
                        {canEdit && (
                          <Button style={{ padding: '6px' }} onClick={save} label={isNew ? '등록' : '저장'}>
                            {isNew ? '등록' : '저장'}
                          </Button>
                        )}
                      </FlexBoxHorizontal>
                    </FlexBoxVertical>

                    {/* 연결 품번 */}
                    {selected && (
                      <FlexBoxVertical style={{ height: 'auto', width: '90%', alignItems: 'flex-start', margin: '10px 0' }}>
                        <Label style={{ margin: '3px 0' }}>
                          <TranslateTypography variant="content2">{`연결 품번 ${products.length}`}</TranslateTypography>
                        </Label>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', color: '#353339', marginTop: '6px' }}>
                          <thead>
                            <tr style={{ textAlign: 'left' }}>
                              <th style={thStyle}><TranslateTypography variant="content2">품번</TranslateTypography></th>
                              <th style={thStyle}><TranslateTypography variant="content2">제품명</TranslateTypography></th>
                              <th style={thStyle}><TranslateTypography variant="content2">복종</TranslateTypography></th>
                              <th style={thStyle}><TranslateTypography variant="content2">디자이너</TranslateTypography></th>
                            </tr>
                          </thead>
                          <tbody>
                            {products.map((p) => (
                              <tr key={p.part_cd} style={{ borderBottom: `1px solid ${palette.border.default}` }}>
                                <td style={tdStyle}>{p.part_cd}</td>
                                <td style={{ ...tdStyle, color: p.in_plan ? '#353339' : '#999' }}>{p.in_plan ? p.prdt_nm : '기획표에 없음'}</td>
                                <td style={tdStyle}>{p.cat || ''}</td>
                                <td style={tdStyle}>{p.dsgnr_nm || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </FlexBoxVertical>
                    )}

                    {/* 업로드 박스 - graphicMaster edit 권한 필요 */}
                    {canEdit && (
                      <>
                        <UploadBox
                          $hasPreview={!!previewImage}
                          onDrop={handleDrop}
                          onDragOver={(event) => event.preventDefault()}
                          onPaste={handlePaste}
                        >
                          <FlexBoxVertical>
                            {previewImage ? (
                              <ImageContainer>
                                <img src={previewImage} alt="미리보기" className="preview-image" />
                                <CloseIcon onClick={handleDeleteImage} className="delete-icon" />
                              </ImageContainer>
                            ) : (
                              <>
                                <LuImagePlus style={{ width: '20%', height: '20%', color: 'rgb(218, 222, 224)' }} />
                                <TranslateTypography variant="content1" style={{ color: 'hsl(0, 0%, 40%)', margin: '5px' }}>
                                  드래그하거나 Ctrl + V를 입력하여
                                </TranslateTypography>
                                <TranslateTypography variant="content1" style={{ color: 'hsl(0, 0%, 40%)' }}>
                                  이미지를 붙여넣으세요
                                </TranslateTypography>
                              </>
                            )}
                          </FlexBoxVertical>
                        </UploadBox>
                        <FlexBoxHorizontal style={{ height: 'auto', padding: '10px 0' }}>
                          <input type="file" accept="image/*" onChange={handleFileChange} ref={fileInputRef} style={{ display: 'none' }} />
                          <Button style={{ marginRight: '10px', padding: '6px' }} onClick={() => fileInputRef.current.click()} label="파일 선택">
                            파일 선택
                          </Button>
                          <Button style={{ padding: '6px' }} onClick={uploadImage} label="업로드">
                            업로드
                          </Button>
                        </FlexBoxHorizontal>
                      </>
                    )}
                  </>
              </FlexBoxVertical>
            }
            ratio={{ left: 0.62, right: 0.38 }}
          />
        </Content>
      </Dashboard>
      </>
      }
    </Page>
  );
}

export default GraphicMaster;

const columns = [
  { field: 'graphic_cd', headerName: '코드', width: 100 },
  { field: 'type', headerName: '유형', width: 60 },
  { field: 'graphic_nm', headerName: '이름', flex: 1, minWidth: 220 },
  { field: 'graphic_dsgnr_nm', headerName: '담당', width: 110 },
  { field: 'product_count', headerName: '연결 품번', width: 90, type: 'number' },
  { field: 'use_yn', headerName: '사용', width: 60 },
  { field: 'reg_dt', headerName: '등록일', width: 110 },
];

const UploadBox = styled(FlexBox)`
  margin: 10px 20px 0 20px;
  border: 2px dashed rgb(218, 222, 224);
  width: 90%;
  height: 300px;
  border-radius: 10px;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: ${({ $hasPreview }) => ($hasPreview ? 'transparent' : 'rgba(218, 222, 224, 0.2)')};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: fit-content;
  height: fit-content;
  overflow: hidden;
  max-height: 260px;

  .preview-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: filter 0.3s ease;
    margin: 5px;
  }

  .delete-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    cursor: pointer;
    display: none;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    padding: 5px;
  }

  &:hover .preview-image {
    filter: brightness(50%);
  }

  &:hover .delete-icon {
    display: block;
  }
`;

const thStyle = { padding: '6px 4px', fontWeight: 'bold', borderBottom: `1.5px solid ${palette.border.default}` };
const tdStyle = { padding: '5px 4px' };

const gridStyle = {
  width: '100%',
  fontSize: '0.8rem',
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: '#F9F9FB !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 'bold',
  },
  '& .Mui-selected': {
    backgroundColor: 'rgba(0, 0, 0, 0.03) !important',
  },
  '& .MuiDataGrid-row': {
    cursor: 'pointer',
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
};
